import React, { useMemo, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { toast } from "sonner";
import DatePicker from "./DatePicker";
import TimeSlotPicker from "./TimeSlotPicker";
import { formatYMD, parseDateTimeLocal } from "../lib/dateLocal";
import { formatBookingError, ONE_WAY_MAX_KM } from "../lib/bookingErrors";

function initialAmendEnd(booking) {
  const dt = parseDateTimeLocal(booking.end_date);
  if (dt) {
    return {
      date: formatYMD(dt),
      time: `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`,
    };
  }
  const datePart = (booking.end_date || "").split("T")[0];
  return { date: datePart, time: "18:00" };
}

export default function AmendRequestForm({ booking, onDone }) {
  const initial = initialAmendEnd(booking);
  const policy = booking.amendment_policy || booking.vehicle_snapshot?.amendment_policy || {};
  const canExtend = policy.allows_return_date_extension === true;
  const canChangeAddress = policy.allows_return_address_change === true && policy.allows_one_way === true;
  const [endDate, setEndDate] = useState(initial.date);
  const [endTime, setEndTime] = useState(initial.time);
  const [dropoffLoc, setDropoffLoc] = useState(booking.dropoff_location || booking.pickup_location || "");
  const [dropoffPostal, setDropoffPostal] = useState(booking.dropoff_postal || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const timeSlots = useMemo(() => {
    const s = [];
    for (let h = 0; h < 24; h++) for (const m of [0, 30])
      s.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    return s;
  }, []);

  const minEndDate = (() => {
    const currentEnd = parseDateTimeLocal(booking.end_date);
    return currentEnd ? formatYMD(currentEnd) : undefined;
  })();

  const buildEndDatePayload = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate || "")) {
      toast.error("Pick a valid return date");
      return null;
    }
    if (!/^\d{2}:\d{2}$/.test(endTime || "")) {
      toast.error("Enter return time as HH:MM");
      return null;
    }
    const combined = `${endDate}T${endTime}`;
    const parsed = parseDateTimeLocal(combined);
    if (!parsed) {
      toast.error("Return date/time is invalid");
      return null;
    }
    const start = parseDateTimeLocal(booking.start_date);
    if (start && parsed.getTime() <= start.getTime()) {
      toast.error("Return must be after pick-up");
      return null;
    }
    if (start && parsed.getTime() - start.getTime() < 30 * 60 * 1000) {
      toast.error("Trip must be at least 30 minutes");
      return null;
    }
    const currentEnd = parseDateTimeLocal(booking.end_date);
    if (currentEnd && parsed.getTime() < currentEnd.getTime()) {
      toast.error("The current return cannot be shortened");
      return null;
    }
    return (booking.end_date || "").includes("T") || (booking.start_date || "").includes("T")
      ? combined
      : endDate;
  };

  const submit = async () => {
    const endPayload = buildEndDatePayload();
    if (!endPayload) return;
    if (!dropoffLoc.trim()) {
      toast.error("Enter a drop-off location");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/bookings/${booking.booking_id}/amend-request`,
        {
          end_date: endPayload,
          dropoff_location: dropoffLoc.trim(),
          dropoff_postal: dropoffPostal.trim() || null,
          reason: reason.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Change request sent to your host");
      onDone?.();
    } catch (e) {
      toast.error(formatBookingError(e.response?.data?.detail) || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg)] text-xs space-y-2" data-testid={`amend-form-${booking.booking_id}`}>
      <div className="font-semibold text-[var(--zc-text)]">Request trip change</div>
      <p className="text-[var(--zc-text-3)]">Propose a new return date, time, or drop-off. Your host must approve before anything changes.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <DatePicker
          label="New return date"
          value={endDate}
          onChange={setEndDate}
          minDate={minEndDate}
          disabled={!canExtend}
          testid="amend-end-date"
          helperText={!canExtend ? "This host does not allow return-date extensions." : "Only a later return can be requested."}
        />
        <div>
          <span className="text-[var(--zc-text-3)] text-xs block mb-1">Return time</span>
          <TimeSlotPicker
            value={endTime}
            onChange={setEndTime}
            slots={timeSlots}
            disabled={!canExtend}
            markAfterHours
            testid="amend-end-time"
          />
        </div>
      </div>
      <label className="block">
        <span className="text-[var(--zc-text-3)]">Return address</span>
        <input value={dropoffLoc} onChange={(e) => setDropoffLoc(e.target.value)} disabled={!canChangeAddress} className="zc-input h-9 px-2 w-full mt-1" placeholder="Drop-off address" data-testid="amend-dropoff" />
      </label>
      <label className="block max-w-xs">
        <span className="text-[var(--zc-text-3)]">Postal / ZIP</span>
        <input value={dropoffPostal} onChange={(e) => setDropoffPostal(e.target.value)} disabled={!canChangeAddress} className="zc-input h-9 px-2 w-full mt-1" placeholder="Postal / ZIP" data-testid="amend-postal" />
      </label>
      {!policy.allows_return_address_change && (
        <div className="zc-hint" data-testid="amend-address-disabled">This host does not allow return-address changes.</div>
      )}
      {policy.allows_return_address_change && !policy.allows_one_way && (
        <div className="zc-hint" data-testid="amend-oneway-disabled">This listing does not allow returns to a different address.</div>
      )}
      {canChangeAddress && dropoffLoc.trim() && (
        <div className="zc-hint" data-testid="amend-oneway-hint">
          Drop-off must be within {ONE_WAY_MAX_KM} km of pick-up.
        </div>
      )}
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="zc-input w-full p-2 h-16" placeholder="Reason (optional)" data-testid="amend-reason" />
      <button type="button" onClick={submit} disabled={busy || (!canExtend && !canChangeAddress)} className="px-3 h-8 zc-btn-primary text-xs">{busy ? "Sending…" : "Submit request"}</button>
    </div>
  );
}
